import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { API_ENDPOINTS, getAuthHeadersMultipart } from '../../config/api';

const ImageUpload = ({ value, onChange, label, folder = 'servicehub' }) => {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(value || '');
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size should be less than 5MB');
      return;
    }

    setError('');
    setUploading(true);

    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('folder', folder);

      const response = await fetch(API_ENDPOINTS.UPLOAD_IMAGE, {
        method: 'POST',
        headers: getAuthHeadersMultipart(),
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to upload image');
      }

      const imageUrl = data.data.url;
      setPreview(imageUrl);
      onChange(imageUrl);
    } catch (err) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setPreview('');
    onChange('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUrlChange = (e) => {
    const url = e.target.value;
    setPreview(url);
    onChange(url);
  };

  return (
    <Container>
      {label && <Label>{label}</Label>}
      
      <UploadArea>
        {preview ? (
          <PreviewContainer>
            <PreviewImage src={preview} alt="Preview" />
            <RemoveButton onClick={handleRemove} type="button">
              ×
            </RemoveButton>
          </PreviewContainer>
        ) : (
          <UploadPlaceholder>
            <UploadIcon>📷</UploadIcon>
            <UploadText>Click to upload or enter URL below</UploadText>
          </UploadPlaceholder>
        )}
        
        <FileInput
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          disabled={uploading}
        />
      </UploadArea>

      {uploading && <UploadProgress>Uploading...</UploadProgress>}
      {error && <ErrorMessage>{error}</ErrorMessage>}

      <UrlInputContainer>
        <UrlLabel>Or enter image URL:</UrlLabel>
        <UrlInput
          type="text"
          placeholder="https://example.com/image.jpg"
          value={preview}
          onChange={handleUrlChange}
          disabled={uploading}
        />
      </UrlInputContainer>
    </Container>
  );
};

export default ImageUpload;

const Container = styled.div`
  width: 100%;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: ${props => props.theme.text};
`;

const UploadArea = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  border: 2px dashed ${props => props.theme.border};
  border-radius: 8px;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${props => props.theme.primary};
    background-color: ${props => props.theme.cardHover || props.theme.cardBg};
  }
`;

const FileInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }
`;

const UploadPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  pointer-events: none;
`;

const UploadIcon = styled.div`
  font-size: 48px;
  margin-bottom: 12px;
`;

const UploadText = styled.p`
  color: ${props => props.theme.textLight};
  font-size: 14px;
`;

const PreviewContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const RemoveButton = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.7);
  color: white;
  border: none;
  font-size: 24px;
  line-height: 1;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #dc3545;
  }
`;

const UploadProgress = styled.div`
  margin-top: 8px;
  color: ${props => props.theme.primary};
  font-size: 14px;
  text-align: center;
`;

const ErrorMessage = styled.div`
  margin-top: 8px;
  color: #dc3545;
  font-size: 14px;
`;

const UrlInputContainer = styled.div`
  margin-top: 16px;
`;

const UrlLabel = styled.label`
  display: block;
  margin-bottom: 6px;
  font-size: 14px;
  color: ${props => props.theme.textLight};
`;

const UrlInput = styled.input`
  width: 100%;
  padding: 10px 15px;
  border: 1px solid ${props => props.theme.border};
  border-radius: 8px;
  font-size: 14px;
  background-color: ${props => props.theme.inputBg || props.theme.cardBg};
  color: ${props => props.theme.text};
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.primary};
    box-shadow: 0 0 0 3px ${props => props.theme.primary}20;
  }

  &:disabled {
    background-color: ${props => props.theme.border};
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${props => props.theme.textLight};
  }
`;
